<i18n>
{
  "fr": {
    "searchControl": "Chercher un contrôle",
    "town": "Commune",
    "street": "Rue",
    "streetNumber": "Numéro de police",
    "noPropertiesFound": "Aucune propriété trouvé",
    "Controls": "Contrôles",
    "controls": "contrôles",
    "loading": "Chargement...",
    "folderNumber": "N° dossier",
    "filter": "Filtrer"
  }
}
</i18n>

<template>
  <b-card body-class="pd-0" header-class="bg-transparent bd-b-0" class="card-hover card-suze">
    <template v-slot:header>
      <h6 class="card-title mg-b-0">{{ $t('Controls') }}</h6>
      <div class="flex">
        <b-button
          size="sm"
          v-b-toggle.user-filter-div
          class="s-btn btn-white mg-r-10"
          type="button"
          id="filter-button"
        >
          <IconFilter class="suzv" />
          <span class="hidden-xs button-filter">
            {{$t('filter')}}
            <i class="fa fa-angle-down fa-lg"></i>
          </span>
          <span class="visible-xs">
            <i class="fa fa-filter"></i>
          </span>
        </b-button>
      </div>
    </template>
    <b-collapse id="user-filter-div" class="animate-show filter-bloc">
      <div class="card-header">
        <Search />
      </div>
    </b-collapse>
    <table class="table table-striped admin-list">
      <thead>
        <tr class="thead-color">
          <th scope="col" @click="sort('folderNumberLong')">
            N° de dossier
            <i
              v-if="sortIcon('folderNumberLong', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('folderNumberLong', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>

          <th scope="col" @click="sort('requestType')">
            Type
            <i
              v-if="sortIcon('requestType', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('requestType', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
          <th scope="col" @click="sort('address.city')">
            Commune
            <i
              v-if="sortIcon('address.city', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('address.city', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
          <th scope="col" @click="sort('address.number', 'ASC')">N°</th>
          <th scope="col" @click="sort('address.street', 'ASC')">Rue</th>
          <th scope="col" @click="sort('dateOfVisit')">
            <i v-if="sortIcon('dateOfVisit', 'DESC')" class="fa fa-sort-desc" aria-hidden="true"></i>
            <i v-if="sortIcon('dateOfVisit', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
            Date de visite
          </th>
          <th scope="col" @click="sort('status.label')">
            Etat
            <i
              v-if="sortIcon('status.label', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('status.label', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>

          <th scope="col">
            Modifié par
          </th>
          <th @click="sort('mailLastGenerateDate')">Date génération courrier
            <i
              v-if="sortIcon('mailLastGenerateDate', 'DESC')"
              class="fa fa-sort-desc"
              aria-hidden="true"
            ></i>
            <i v-if="sortIcon('mailLastGenerateDate', 'ASC')" class="fa fa-sort-asc" aria-hidden="true"></i>
          </th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(control, index) in controls" :key="index">
          <td>{{ getControlFolderNumber(control) }}</td>

          <td>{{ control.requestType }}</td>
          <td>{{ toUpperCase(control.address.city) }}</td>
          <td>{{control.address.number}}</td>
          <td>{{control.address.street}}</td>
          <td>{{ formatDate(control.data.dateOfVisit) }}</td>
          <td>{{ control.currentStatus.status.label }}</td>
          <td>{{ control.currentStatus.applicationUser.firstName }} {{ control.currentStatus.applicationUser.lastName }}</td>
          <td>{{ formatDate(getGenerateMailDate(control)) }}</td>
          <td>
            <b-link @click="onDetailControl(control)">
              <v-icon name="eye" />
            </b-link>
            <b-link v-if="isWrite(control.currentStatus.status.id)" @click="onEditControl(control)">
              <v-icon name="edit" />
            </b-link>
            <b-link
              v-show="isMailPermitted(control.currentStatus.status.id)"
              @click="onMailManagementIndex(control)"
            >
              <v-icon name="file-contract" />
            </b-link>
          </td>
        </tr>
      </tbody>
    </table>
    <b-row v-if="!loaderData">
      <b-col md="8">
        <paginate
          :page-count="getPageCount"
          :click-handler="getControlsByPage"
          :prev-text="'«'"
          :next-text="'»'"
          container-class="pagination"
        ></paginate>
      </b-col>
      <b-col md="4">
        <a
          type="submit"
          class="btn btn-cta s-btn"
          v-bind:href="exportCsv()"
          target="_blank"
          size="sm"
        >Export de l'ensemble des contrôles</a>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import IconFilter from '../icons/IconFilter.vue';
import Search from '../ControlSearch';
import { mapState, mapGetters } from 'vuex';
import { formatDate, formatDateTimestamp } from '../../utils/formatDate';

export default {
  name: 'controls',
  components: {
    Search,
    IconFilter,
  },
  data() {
    return {
      loaderData: true,
      order: 'ASC',
      sorterFields: '',
    };
  },
  computed: {
    ...mapState({ search: state => state.controlsList.search }),
    ...mapState({ controls: state => state.controlsList.controlsList }),
    ...mapGetters(['getNotif', 'getPageCount']),
  },
  methods: {
    exportCsv() {
      let filters = [];
      let link = '/fr/syntheses/query.csv?&synthesisFilterSetType=controls&v=' + formatDateTimestamp() + '&';
      if (this.search.folderNumber) {
        filters.push('filterFields[]=folderNumber=' + this.search.folderNumber);
      }
      if (this.search.requestType) {
        filters.push('filterFields[]=controls_0_requestType=' + this.search.requestType);
      }
      if (this.search.status) {
        filters.push('filterFields[]=controls_0_currentStatus_status_id=' + this.search.status);
      }

      if (this.search.addressNumber) {
        filters.push('filterFields[]=address_numberStreet=' + this.search.addressNumber);
      }

      if (this.search.motifTypes) {
        filters.push('filterFields[]=controls_0_data_typeOfControl_0_id=' + this.search.motifTypes);
      }

      if (this.search.addressStreet) {
        filters.push(`filterFields[]=address_street%25${this.search.addressStreet}`);
      }

      if (this.search.town) {
        filters.push('filterFields[]=address_town=' + this.search.town.internalLabel);
      }

      if (this.search.dateOfVisitFrom) {
        filters.push('filterFields[]=controls_0_data_dateOfVisit>' + this.search.dateOfVisitFrom);
      }
      if (this.search.dateOfVisitTo) {
        filters.push('filterFields[]=controls_0_data_dateOfVisit<' + this.search.dateOfVisitTo);
      }
      if (this.search.protractor) {
        filters.push('filterFields[]=controls_0_data_protractor=' + this.search.protractor);
      }

      if (filters.length > 0) {
        link += filters.join('&');
      }

      return link;
    },
    formatDate(value) {
      return formatDate(value);
    },
    formatDateTimestamp: function() {
      return formatDateTimestamp();
    },
    toUpperCase(value) {
      if (value) {
        return value.toUpperCase();
      }
    },
    isWrite(status) {
      let visible = {
        new: true,
        inProgress: true,
        waitingForValidation: false,
        closed: false,
        generatedMail: false,
        validated: false,
      };
      return visible[status];
    },
    isMailPermitted(status) {
      let visible = {
        new: false,
        inProgress: false,
        waitingForValidation: false,
        closed: true,
        generatedMail: true,
        validated: true,
      };
      return visible[status];
    },
    onDetailControl(control) {
      const type = control.requestType.toLowerCase();

      const propertyId = this.getControlPropertyId(control);

      this.$router.push({
        name: `${type}_detail`,
        params: { controlId: control.id, propertyId: propertyId },
      });
    },
    onEditControl(control) {
      const type = control.requestType.toLowerCase();

      const propertyId = this.getControlPropertyId(control);

      this.$router.push({
        name: `${type}_edit`,
        params: { controlId: control.id, propertyId: propertyId },
      });
    },
    onMailManagementIndex(control) {

      const propertyId = this.getControlPropertyId(control);

      this.$router.push({
        name: `MailManagementIndex`,
        params: { controlId: control.id, propertyId: propertyId },
      });
    },
    sortIcon(fieldName, order) {
      return this.sorterFields.sortField == fieldName && this.order == order;
    },

    getControls() {
      this.$store.dispatch('fetchControlsList').then(() => (this.loaderData = false));
    },
    getControlsByPage(pageNum) {
      this.$store
        .dispatch('fetchControlsList', {
          page: pageNum,
          sortField: this.sorterFields.sortField,
          order: this.sorterFields.order,
        })
        .then(() => (this.loaderData = false));
    },
    getControlFolderNumber(control) {
      let folderNumber = control.context.find(context => context.type === 'folderNumber');

      if (typeof folderNumber !== 'object' || typeof folderNumber.id === 'undefined') {
        return null;
      }

      return folderNumber.id;
    },

    getGenerateMailDate(control) {
      return control.data.mailLastGenerateDate;
    },

    getControlPropertyId(control) {
      let property = control.context.find(context => context.type === 'property');

      if (typeof property !== 'object' || typeof property.id === 'undefined') {
        return null;
      }

      return property.id;
    },
    sort: function(fieldName) {
      this.order == 'ASC' ? (this.order = 'DESC') : (this.order = 'ASC');
      this.sorterFields = { sortField: fieldName, order: this.order };
      this.$store
        .dispatch('fetchControlsList', { sortField: fieldName, order: this.order })
        .then(() => (this.loaderData = false));
    },
  },
  created: function() {
    this.getControls();
  },
};
</script>

<style scoped>
.frm-c {
  height: 38px !important;
  font-size: 15px !important;
}
.mgt-25 {
  margin-top: 25px !important;
}

.admin-list > thead > tr > th {
  padding-left: 14px !important;
  padding-right: 14px !important;
  vertical-align: center !important;
}

.admin-list > tbody > tr > td {
  padding-left: 14px !important;
  padding-right: 14px !important;
}
</style>
